import { log, logV } from "../../shared/services/debug-logger";
import {
  TECH_FORM_CACHE_MODES,
  TECH_FORM_STORAGE_OPTIONS,
  TECH_FORM_VESSEL_CACHE_STORAGE_KEYS,
} from "../models/tech-form-vessel-cache.constants";
import type { TechFormCachedDraft, TechFormVesselCacheIdentity } from "../models/tech-form-vessel-cache.models";
import {
  getLegacyTechFormVesselCacheKey,
  getTechFormVesselCacheIdentity,
  getTechFormVesselCacheKey,
} from "../utils/get-tech-form-vessel-cache-key.utils";
import { reviveTq5PortCallDate } from "../utils/revive-tq5-port-call-date.utils";
import {
  techFormStepCountAtom,
  tq1VesselDetailsAtom,
  tq2DesignDetailsAtom,
  tq3DraftAtom,
  tq3DraftTableAtom,
  tq4AntiFoulingDetailsAtom,
  tq5PortCallsAtom,
  tq6SecaAtom,
  tq7SlowStreamingAtom,
  tq8PowerManagementAtom,
  tq8PowerManagementTableAtom,
} from "./tech-form.atoms";
import { atom } from "jotai";
import { atomWithStorage, createJSONStorage, RESET } from "jotai/utils";

const vesselDraftsStorage = createJSONStorage<Record<string, TechFormCachedDraft>>(() => localStorage, {
  reviver: reviveTq5PortCallDate,
});

const activeVesselAtom = atomWithStorage<TechFormVesselCacheIdentity | null>(
  TECH_FORM_VESSEL_CACHE_STORAGE_KEYS.activeVessel,
  null,
  undefined,
  TECH_FORM_STORAGE_OPTIONS,
);
const vesselDraftsAtom = atomWithStorage<Record<string, TechFormCachedDraft>>(
  TECH_FORM_VESSEL_CACHE_STORAGE_KEYS.drafts,
  {},
  vesselDraftsStorage,
  TECH_FORM_STORAGE_OPTIONS,
);
const sectionAtoms = {
  tq1VesselDetails: tq1VesselDetailsAtom,
  tq2DesignDetails: tq2DesignDetailsAtom,
  tq3Draft: tq3DraftAtom,
  tq3DraftTable: tq3DraftTableAtom,
  tq4AntiFoulingDetails: tq4AntiFoulingDetailsAtom,
  tq5PortCalls: tq5PortCallsAtom,
  tq6Seca: tq6SecaAtom,
  tq7SlowStreaming: tq7SlowStreamingAtom,
  tq8PowerManagement: tq8PowerManagementAtom,
  tq8PowerManagementTable: tq8PowerManagementTableAtom,
};

// Select all sections together before any editor can reuse a different vessel's cache.
export const selectTechFormVesselDraftAtom = atom(null, (get, set, identity: TechFormVesselCacheIdentity) => {
  const selectedKey = getTechFormVesselCacheKey(identity);
  if (!selectedKey) return;
  const currentIdentity =
    getTechFormVesselCacheIdentity(get(activeVesselAtom), TECH_FORM_CACHE_MODES.editable) ??
    getTechFormVesselCacheIdentity(get(tq1VesselDetailsAtom), TECH_FORM_CACHE_MODES.editable);
  const currentKey = getTechFormVesselCacheKey(currentIdentity);
  if (currentKey === selectedKey) {
    logV("STATE - selectTechFormVesselDraftAtom - unchanged");
    set(activeVesselAtom, identity);
    return;
  }

  const drafts = { ...get(vesselDraftsAtom) };
  if (currentKey) {
    drafts[currentKey] = {
      step: get(techFormStepCountAtom),
      sections: Object.fromEntries(Object.entries(sectionAtoms).map(([name, sectionAtom]) => [name, get(sectionAtom)])),
    };
  }
  const legacySelectedKey =
    identity.mode === TECH_FORM_CACHE_MODES.editable ? getLegacyTechFormVesselCacheKey(identity) : null;
  const selected = drafts[selectedKey] ?? (legacySelectedKey ? drafts[legacySelectedKey] : undefined);
  log("STATE - selectTechFormVesselDraftAtom", { imoNumber: identity.imoNumber, restored: !!selected });
  // The active draft lives in the existing atoms; do not retain an older copy
  // that could be restored after submission clears those atoms.
  delete drafts[selectedKey];
  if (legacySelectedKey) delete drafts[legacySelectedKey];
  set(vesselDraftsAtom, drafts);
  Object.entries(sectionAtoms).forEach(([name, sectionAtom]) => set(sectionAtom, selected?.sections[name] ?? RESET));
  set(techFormStepCountAtom, selected?.step ?? RESET);
  set(activeVesselAtom, identity);
});

// Remove only the vessel that completed submission. Another vessel may have
// become active while the submission request or completion message was pending.
export const clearSubmittedTechFormVesselDraftAtom = atom(null, (get, set, identity: TechFormVesselCacheIdentity) => {
  const submittedKey = getTechFormVesselCacheKey(identity);
  if (!submittedKey) return;

  const drafts = { ...get(vesselDraftsAtom) };
  delete drafts[submittedKey];
  if (identity.mode === TECH_FORM_CACHE_MODES.editable) {
    const legacySubmittedKey = getLegacyTechFormVesselCacheKey(identity);
    if (legacySubmittedKey) delete drafts[legacySubmittedKey];
  }
  set(vesselDraftsAtom, drafts);

  const activeIdentity =
    getTechFormVesselCacheIdentity(get(activeVesselAtom), TECH_FORM_CACHE_MODES.editable) ??
    getTechFormVesselCacheIdentity(get(tq1VesselDetailsAtom), TECH_FORM_CACHE_MODES.editable);
  const activeKey = getTechFormVesselCacheKey(activeIdentity);
  if (activeKey !== submittedKey) return;

  log("STATE - clearSubmittedTechFormVesselDraftAtom");
  Object.values(sectionAtoms).forEach((sectionAtom) => set(sectionAtom, RESET));
  set(techFormStepCountAtom, RESET);
  set(activeVesselAtom, RESET);
});
